// src/pages/AgentPair.jsx — "Pair this device" landing page for bridge agent
//
// URL:  /agent-pair?code=XXXX-YYYY
//
// Flow:
//   1. Agent (Electron) opens browser to this URL with ?code=... in query
//   2. Page reads the code; if user is not logged in it stashes the code
//      in sessionStorage and redirects to "/" (which your App renders as
//      LoginPage when !user). After MSAL completes, App will auto-detect
//      the stashed code and redirect back to /agent-pair?code=...
//   3. Logged-in user sees confirmation card → "Pair this device"
//   4. POST /agents/pairing/claim → success screen → user closes tab
//   5. Agent polls and picks up the claim → connects → tray goes green

import React, { useState, useEffect, useCallback } from 'react';
import { apiPost } from '../api';
import '../styles/AgentPair.css';

const STASH_KEY = 'auditpros_pending_pair_code';

function getCodeFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('code') || '').trim().toUpperCase();
    return code || null;
  } catch {
    return null;
  }
}

function getLoggedInUser() {
  try {
    const saved = localStorage.getItem('auditpros_user');
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    const account = parsed?.account;
    if (!account) return null;
    const email = account?.idTokenClaims?.email
      || account?.idTokenClaims?.preferred_username
      || account?.idTokenClaims?.emails?.[0]
      || account?.username || account?.email;
    const name = account?.name
      || account?.idTokenClaims?.name
      || (email ? email.split('@')[0] : '');
    return email ? { email, name } : null;
  } catch {
    return null;
  }
}

function Shell({ children }) {
  return <div className="ap-shell">{children}</div>;
}

function Card({ children }) {
  return <div className="ap-card fade-in">{children}</div>;
}

export default function AgentPair() {
  const [code] = useState(getCodeFromUrl);
  const [user, setUser] = useState(getLoggedInUser);
  const [status,     setStatus]     = useState('idle');  // idle | pairing | success | error
  const [errorMsg,   setErrorMsg]   = useState(null);
  const [resultInfo, setResultInfo] = useState(null);

  useEffect(() => {
    if (user || !code) return;
    try { sessionStorage.setItem(STASH_KEY, code); } catch {}
    const t = setTimeout(() => { window.location.href = '/'; }, 400);
    return () => clearTimeout(t);
  }, [user, code]);

  const handlePair = useCallback(async () => {
    if (!code) return;
    setStatus('pairing');
    setErrorMsg(null);
    try {
      const result = await apiPost('/agents/pairing/claim', { deviceCode: code });
      setResultInfo(result);
      setStatus('success');
      try { sessionStorage.removeItem(STASH_KEY); } catch {}
    } catch (err) {
      setErrorMsg(err?.message || 'Pairing failed. Please try again from the agent.');
      setStatus('error');
    }
  }, [code]);

  if (!code) {
    return (
      <Shell>
        <Card>
          <h1 className="ap-title">Invalid pairing link</h1>
          <p className="ap-body">
            This pairing link is missing or malformed. Please open the AuditPrOS
            Sync agent on your computer and try again — the agent will generate
            a fresh link.
          </p>
        </Card>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell>
        <Card>
          <h1 className="ap-title">Sign in to continue</h1>
          <p className="ap-body">
            Redirecting you to sign in. You'll return here to pair the device
            after signing in.
          </p>
          <div className="ap-code-box">
            Pairing code: <strong>{code}</strong>
          </div>
        </Card>
      </Shell>
    );
  }

  if (status === 'success') {
    return (
      <Shell>
        <Card>
          <div className="ap-icon ap-icon--success">✓</div>
          <h1 className="ap-title">Device paired successfully</h1>
          <p className="ap-body">
            <strong>{resultInfo?.deviceName || 'Your device'}</strong> is now
            connected to your AuditPrOS account. You can close this tab — the
            sync agent on your computer will connect automatically within a few
            seconds.
          </p>
          <p className="ap-body-muted" style={{ marginTop: 20 }}>
            Look for the green icon in your Windows system tray to confirm.
          </p>
          <div className="ap-actions ap-actions--lg">
            <button onClick={() => window.close()} className="ap-btn-primary">
              Close this tab
            </button>
            <button onClick={() => { window.location.href = '/'; }} className="ap-btn-secondary">
              Go to AuditPrOS
            </button>
          </div>
        </Card>
      </Shell>
    );
  }

  if (status === 'error') {
    return (
      <Shell>
        <Card>
          <div className="ap-icon ap-icon--error">!</div>
          <h1 className="ap-title">Pairing failed</h1>
          <p className="ap-body">{errorMsg}</p>
          <p className="ap-body-muted" style={{ marginTop: 14 }}>
            The pairing code may have expired (they're valid for 15 minutes) or
            already been used. Please open AuditPrOS Sync on your computer and
            retry — the agent will generate a fresh code.
          </p>
          <div className="ap-actions">
            <button
              onClick={() => { setStatus('idle'); setErrorMsg(null); }}
              className="ap-btn-secondary"
            >
              Try again
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              className="ap-btn-secondary"
            >
              Go to AuditPrOS
            </button>
          </div>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card>
        <div className="ap-badge">AuditPrOS Sync</div>
        <h1 className="ap-title">Pair this device?</h1>
        <p className="ap-body">
          The sync agent on your computer is requesting to connect to your
          AuditPrOS account. Once paired, AuditPrOS can sync data from your
          local Tally installation securely.
        </p>

        <div className="ap-details-box">
          <div className="ap-detail-row">
            <span className="ap-detail-label">Signed in as</span>
            <span className="ap-detail-value">{user.email}</span>
          </div>
          <div className="ap-detail-row">
            <span className="ap-detail-label">Pairing code</span>
            <span className="ap-detail-value ap-detail-value--mono">{code}</span>
          </div>
        </div>

        <p className="ap-body-muted">
          Only pair devices you recognise. If you did not start this pairing on
          a trusted computer, click <strong>Cancel</strong>.
        </p>

        <div className="ap-actions">
          <button
            onClick={handlePair}
            disabled={status === 'pairing'}
            className="ap-btn-primary"
          >
            {status === 'pairing' ? 'Pairing…' : 'Pair this device'}
          </button>
          <button
            onClick={() => { window.location.href = '/'; }}
            disabled={status === 'pairing'}
            className="ap-btn-secondary"
          >
            Cancel
          </button>
        </div>
      </Card>
    </Shell>
  );
}
